import * as devToolsExtension from '@redux-devtools/extension';
import localforage from 'localforage';
import { omit } from 'es-toolkit';
import { applyMiddleware, compose, createStore } from 'redux';
import type { Action, Reducer, UnknownAction } from 'redux';
import * as logger from 'redux-logger';
import { createMigrate, createTransform, persistReducer, persistStore } from 'redux-persist';
import type { PersistedState } from 'redux-persist';
import type { MigrationManifest } from 'redux-persist';
import { thunk } from 'redux-thunk';

import { reduxDevLogging, target } from '/shared/util/Statics';

type PersistedRecordValue =
  | string
  | number
  | boolean
  | Date
  | PersistedRecord
  | PersistedRecordValue[]
  | null
  | undefined;
interface PersistedRecord {
  [key: string]: PersistedRecordValue;
}

export default function create<S extends object, A extends Action<string>, P = Partial<S>>(
  reducers: Reducer<S, A, P>,
  persistKey = '',
) {
  const omitKeys = [
    'error',
    'errors',
    'loading',
    'fetching',
    'adding',
    'saving',
    'calculating',
    'movingDrafts',
    'modal',
    'editingItemId',
    'hasInitiallySyncedApplications',
    'fetchedPackages',
    'fetchingPackages',
    'fetchedAnnualUpdates',
    'fetchingAnnualUpdates',
    'countries',
    'comparison',
    'lockErrorApplicationId',
    'applicationLoadError',
    'formStateLoadError',
    'loadErrorApplicationId',
  ];

  const blacklistTransform = createTransform((inboundState: PersistedRecord) => {
    return omit(inboundState, omitKeys);
  });

  const asRecord = (value: PersistedState | PersistedRecordValue): PersistedRecord =>
    value != null && typeof value === 'object' ? (value as PersistedRecord) : {};

  const migrations: Record<number, (state: PersistedState) => PersistedRecord> = {
    1: (state: PersistedState) => {
      const s = asRecord(state);
      return {
        ...s,
        location: {
          ...asRecord(s.location),
          countries: [],
        },
      };
    },
    2: (state: PersistedState) => {
      return {
        ...asRecord(state),
        location: {
          fetching: false,
          fetched: false,
          saved: false,
          current: null,
          error: null,
        },
      };
    },
    3: (state: PersistedState) => {
      return {
        ...asRecord(state),
        location: {
          fetching: false,
          fetched: false,
          saved: false,
          current: null,
          error: null,
        },
      };
    },
    4: (state: PersistedState) => {
      const s = asRecord(state);
      return {
        ...s,
        flags: {
          ...asRecord(s.flags),
          countryMarkets: [],
          fetched: false,
        },
      };
    },
    5: (state: PersistedState) => {
      const s = asRecord(state);
      return {
        ...s,
        flags: {
          ...asRecord(s.flags),
          countryMarkets: null,
          fetched: false,
        },
      };
    },
    6: (state: PersistedState) => {
      const s = asRecord(state);
      return {
        ...s,
        flags: {
          ...asRecord(s.flags),
          countryMarkets: null,
          fetched: false,
        },
        location: {
          fetching: false,
          fetched: false,
          saved: false,
          current: null,
          error: null,
        },
      };
    },
    7: (state: PersistedState) => {
      const s = asRecord(state);
      return {
        ...s,
        flags: {
          ...asRecord(s.flags),
          countryMarkets: null,
          fetched: false,
        },
        location: {
          fetching: false,
          fetched: false,
          saved: false,
          current: null,
          error: null,
        },
      };
    },
    8: (state: PersistedState) => {
      const s = asRecord(state);
      return {
        ...s,
        flags: {
          ...asRecord(s.flags),
          countryMarkets: null,
          fetched: false,
        },
        location: {
          fetching: false,
          fetched: false,
          saved: false,
          current: null,
          error: null,
        },
      };
    },
    9: (state: PersistedState) => {
      return {
        ...asRecord(state),
        airports: {
          items: null,
          fetching: false,
          fetched: false,
          error: null,
          token: null,
        },
      };
    },
    10: (state: PersistedState) => {
      return {
        ...asRecord(state),
        staticProps: {
          fetched: false,
          questionStatics: [],
        },
      };
    },
    11: (state: PersistedState) => {
      return {
        ...asRecord(state),
        staticProps: {
          fetched: false,
          questionStatics: [],
        },
      };
    },
    12: (state: PersistedState) => {
      return {
        ...asRecord(state),
        staticProps: {
          fetched: false,
          questionStatics: [],
        },
      };
    },
    13: (state: PersistedState) => {
      return {
        ...asRecord(state),
        railways: {
          items: null,
          fetching: false,
          fetched: false,
          error: null,
          token: null,
        },
      };
    },
    14: (state: PersistedState) => {
      return {
        ...asRecord(state),
        staticProps: {
          fetched: false,
          egrids: [],
          countryRegions: [],
          questionStatics: [],
        },
      };
    },
  };

  type PersistStateValue = NonNullable<PersistedState>['_persist'];

  const hasPersistState = (state: PersistedState): state is PersistedRecord & { _persist: PersistStateValue } =>
    state != null && typeof state === 'object' && '_persist' in state;

  const migrationManifest: MigrationManifest = {};
  Object.entries(migrations).forEach(([version, migrate]) => {
    migrationManifest[Number(version)] = (state: PersistedState) => {
      if (!hasPersistState(state)) {
        return undefined;
      }
      const persistedState = state;
      return {
        ...migrate(persistedState),
        _persist: persistedState._persist,
      };
    };
  });

  const persistConfig = {
    key: (target as typeof target & { value: string }).value + persistKey,
    version: 14,
    migrate: createMigrate(migrationManifest, { debug: false }),
    storage: localforage,
    transforms: [blacklistTransform],
  };

  const reducerForPersist: Reducer<S, UnknownAction> = (state, action) => reducers(state, action as A);
  const persistedReducers = persistReducer(persistConfig, reducerForPersist);

  const composed = reduxDevLogging
    ? devToolsExtension.composeWithDevTools(applyMiddleware(thunk, logger.createLogger()))
    : compose(applyMiddleware(thunk));

  function HigherOrderReducer<TState extends object>(
    reducer: Reducer<TState, UnknownAction>,
    initialState?: TState,
  ): Reducer<TState, UnknownAction> {
    const isReplaceWholeStateAction = (action: UnknownAction): action is UnknownAction & { data?: TState } =>
      action.type === 'REPLACE_WHOLE_STATE';
    let replacementState = initialState;
    return function (state: TState | undefined, action: UnknownAction) {
      switch (action.type) {
        case 'REPLACE_WHOLE_STATE':
          if (isReplaceWholeStateAction(action)) {
            replacementState = action.data;
          }
          return replacementState ?? reducer(state, action);
        default:
          return reducer(state, action);
      }
    };
  }
  const store = createStore(HigherOrderReducer(persistedReducers), composed);
  const persistor = persistStore(store);

  return { store, persistor };
}
